import { useQuery } from '@tanstack/react-query';
import type { ListResponse, ProjectRefResponse } from '@proerp/shared';
import { apiRequest } from './apiClient';

/**
 * Nachschlagewerk „Projekt-ID → Nummer + Name" (`GET /v1/projects/refs`).
 * Grundlage dafür, dass in der ganzen App neben der Projektnummer der Projektname
 * steht (Wunsch Tech-Lead 2026-07-28), ohne den Namen als Snapshot in jede Tabelle
 * zu kopieren — eine Umbenennung wirkt damit sofort überall.
 *
 * Die Antwort ist absichtlich winzig (drei Spalten je Projekt) und wird lange
 * gecacht: viele Seiten fragen sie an, sie ändert sich aber selten. Fehlschläge
 * (z. B. fehlendes `projects.read`) bleiben still — die Anzeige fällt dann auf die
 * Projektnummer allein zurück, statt einen Fehler zu zeigen.
 */
export function useProjectRefs(): Map<string, { code: string; name: string }> {
  const { data } = useQuery({
    queryKey: ['projects', 'refs'],
    queryFn: () => apiRequest<ListResponse<ProjectRefResponse>>('/v1/projects/refs'),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const rows = data?.data;
  // Map bei jedem Render neu zu bauen ist billig (wenige hundert Einträge) — dafür
  // gibt es keinen Zustand, der veralten kann.
  return new Map((rows ?? []).map((row) => [row.id, { code: row.code, name: row.name }]));
}
